import React, { useState } from "react";
import { Folder, Pin, File as FileIcon, MoreVertical, Download, Trash2, Edit2, Copy, PinOff, Loader2, Eye } from "lucide-react";
import { UIRoomItem } from "../../../models/RoomItem";
import { Util } from "../../../services/Utils/Util";
import { downloadFile } from "../../../services/Utils/UtilDownload";
import { UtilApp } from "../../../services/Utils/UtilApp";
import { My } from "../../../services/My";
import { getDisplayName } from "../../../services/Utils/userDisplayName";
import { Checkbox } from "../../../components/ui/checkbox";
import { cn } from "../../../lib/utils";
import { Fire } from "../../../services/FireClient";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../../../components/ui/dropdown-menu";
import { Trans, t } from '@lingui/macro';
import { useToast } from "../../../components/ui/use-toast";
import { RoomItem } from "../../../models/RoomItem";
import { DocumentViewerModal, isViewerSupported } from "../../components/DocumentViewerModal";
import { UserAvatar } from "../../components/UserAvatar";

interface FileListItemProps {
    uitem: UIRoomItem;
    viewMode: 'list' | 'grid';
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    onMoveFolder?: () => void;
    onPreview?: (url: string) => void;
    canEdit?: boolean;
    isMobile?: boolean;
    onDelete?: () => void;
}

// 파일 확장자 추출 (최대 4글자)
function getFileExtension(filename: string | undefined): string {
    if (!filename) return '';
    const parts = filename.split('.');
    if (parts.length < 2) return '';
    const ext = parts[parts.length - 1].toUpperCase();
    return ext.length <= 4 ? ext : ext.substring(0, 4);
}

// 확장자별 배경색
function getExtensionColor(ext: string): string {
    const colors: { [key: string]: string } = {
        'PDF': 'bg-destructive',
        'DOC': 'bg-blue-500',
        'DOCX': 'bg-blue-500',
        'XLS': 'bg-green-600',
        'XLSX': 'bg-green-600',
        'PPT': 'bg-primary',
        'PPTX': 'bg-primary',
        'ZIP': 'bg-yellow-600',
        'RAR': 'bg-yellow-600',
        'TXT': 'bg-muted/500',
        'CSV': 'bg-green-500',
        'MP3': 'bg-purple-500',
        'MP4': 'bg-pink-500',
        'MOV': 'bg-pink-500',
    };
    return colors[ext] || 'bg-muted-foreground';
}

// 파일 아이콘 컴포넌트
const FileIconWithExt: React.FC<{ filename?: string; size?: 'sm' | 'md' }> = ({ filename, size = 'md' }) => {
    const ext = getFileExtension(filename);
    const bgColor = getExtensionColor(ext);
    const iconSize = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
    const textSize = size === 'sm' ? 'text-meta-sm' : 'text-meta-sm';

    return (
        <div className={cn("relative flex items-center justify-center rounded bg-muted", iconSize)}>
            <FileIcon className={cn("text-muted-foreground", size === 'sm' ? 'w-5 h-5' : 'w-6 h-6')} />
            {ext && (
                <span className={cn(
                    "absolute bottom-0 left-0 right-0 text-center text-on-media-text font-bold rounded-b px-0.5",
                    textSize, bgColor
                )}>
                    {ext}
                </span>
            )}
        </div>
    );
};

// UUID 생성
function uuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export const FileListItem: React.FC<FileListItemProps> = ({
    uitem, checked, onCheckedChange, onMoveFolder, onPreview, canEdit, isMobile = false, onDelete
}) => {
    const { toast } = useToast();
    const item = uitem.item;
    const isFolder = item.type === 'folder';
    const isMessageFolder = (uitem.itemType as string) === 'MessageFolder';
    const isImage = item.url && Util.isImageUrl(item.url);
    const [isOpening, setIsOpening] = useState(false);
    const viewerSupported = !isFolder && isViewerSupported(item.title || item.file?.filename || '');
    const [docViewerOpen, setDocViewerOpen] = useState(false);
    const [docViewerUrl, setDocViewerUrl] = useState('');

    const handleOpen = async () => {
        if (isFolder) {
            onMoveFolder?.();
            return;
        }

        // CrossViewer 지원 파일이면 뷰어로 열기
        if (viewerSupported) {
            setIsOpening(true);
            try {
                const res = await Fire.item.getRoomItemDownloadURL(item.roomId, item.id);
                if (res.status === 'SUCCESS' && res.url) {
                    setDocViewerUrl(res.url);
                    setDocViewerOpen(true);
                } else {
                    toast({
                        variant: "destructive",
                        title: t`Error`,
                        description: res.status === 'NOT_MEMBER' ? t`You don't have permission.` : t`File not found or expired.`,
                    });
                }
            } catch (error) {
                console.error("Failed to get download URL", error);
            } finally {
                setIsOpening(false);
            }
            return;
        }

        // 이미지인 경우 미리보기
        if (isImage && onPreview && item.url) {
            onPreview(item.url);
            return;
        }

        // 그 외: 다운로드
        await handleDownload();
    };

    const handleDownload = async () => {
        setIsOpening(true);
        try {
            const res = await Fire.item.getRoomItemDownloadURL(item.roomId, item.id);
            if (res.status === 'SUCCESS' && res.url) {
                await downloadFile(res.url, item.title || item.file?.filename || '');
            } else {
                toast({
                    variant: "destructive",
                    title: t`Download Failed`,
                    description: res.status === 'NOT_MEMBER' ? t`You don't have permission.` : t`File not found or expired.`,
                });
            }
        } catch (error) {
            console.error("Failed to get download URL", error);
            toast({
                variant: "destructive",
                title: t`Error`,
                description: (error as Error).message,
            });
        } finally {
            setIsOpening(false);
        }
    };

    // 공통 드롭다운 메뉴 아이템
    const DropdownItems = () => (
        <>
            {item.type !== 'folder' && (
                <>
                    {viewerSupported && (
                        <DropdownMenuItem onClick={handleOpen} className="gap-2" disabled={isOpening}>
                            <Eye className="w-4 h-4" />
                            <span><Trans>Preview</Trans></span>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleDownload} className="gap-2" disabled={isOpening}>
                        {isOpening ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        <span><Trans>Download</Trans></span>
                    </DropdownMenuItem>
                </>
            )}

            {item.roomId !== My.user.id && (
                <DropdownMenuItem onClick={async () => {
                    let copy = Util.pojo(item) as RoomItem;
                    copy.id = uuid();
                    copy.roomId = My.user.id;
                    copy.pinned = false;
                    copy.created = new Date();
                    await Fire.item.saveRoomItem(copy);
                    toast({ title: "Copied to My Channel" });
                }} className="gap-2">
                    <Copy className="w-4 h-4" />
                    <span><Trans>Copy to My Channel</Trans></span>
                </DropdownMenuItem>
            )}

            {canEdit && (
                <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => {
                        const newTitle = prompt("Edit title", item.title);
                        if (newTitle && newTitle !== item.title) {
                            item.title = newTitle;
                            Fire.item.saveRoomItem(item);
                        }
                    }} className="gap-2">
                        <Edit2 className="w-4 h-4" />
                        <span><Trans>Edit</Trans></span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => {
                        item.pinned = !item.pinned;
                        Fire.item.saveRoomItem(item);
                    }} className="gap-2">
                        {item.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                        <span>{item.pinned ? <Trans>Unpin</Trans> : <Trans>Pin</Trans>}</span>
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => {
                        onDelete?.();
                    }} className="gap-2 text-destructive focus:text-destructive focus:bg-destructive/10">
                        <Trash2 className="w-4 h-4" />
                        <span><Trans>Delete</Trans></span>
                    </DropdownMenuItem>
                </>
            )}
        </>
    );

    // PC/Tablet: 테이블 행 형식
    if (!isMobile) {
        return (
            <>
                <div className="flex items-center gap-3 px-3 py-2 bg-background hover:bg-tbl-hover border-b border-tbl-divider transition-all group" data-node-id="115:16713">
                    <Checkbox
                        checked={checked}
                        onCheckedChange={onCheckedChange}
                        disabled={isMessageFolder}
                        className={cn("w-4 h-4 shrink-0", isMessageFolder && "opacity-20")}
                    />

                    <div className="cursor-pointer shrink-0 relative" onClick={handleOpen}>
                        {isFolder ? (
                            <div className="w-8 h-8 flex items-center justify-center rounded bg-muted text-primary">
                                <Folder className="w-5 h-5 fill-current" />
                            </div>
                        ) : isImage ? (
                            <div className="w-8 h-8 rounded overflow-hidden bg-muted">
                                <img src={item.url} className="w-full h-full object-contain" alt={item.title} />
                            </div>
                        ) : (
                            <FileIconWithExt filename={item.title || item.file?.filename} size="sm" />
                        )}
                        {isOpening && (
                            <div className="absolute inset-0 bg-background/60 flex items-center justify-center rounded">
                                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                            </div>
                        )}
                    </div>

                    <div className="flex-[4] min-w-0 font-normal text-tbl-text-primary truncate cursor-pointer hover:text-primary text-sm pl-1" onClick={handleOpen}>
                        {item.title || item.url}
                    </div>

                    {!isMessageFolder && (
                        <div className="w-[144px] shrink-0 flex items-center gap-1.5 px-4" data-node-id="115:16719">
                            <UserAvatar src={item.user.photoUrl} name={getDisplayName(item.user)} className="w-5 h-5" />
                            <div className="text-sm text-label truncate">{getDisplayName(item.user)}</div>
                        </div>
                    )}

                    {!isMessageFolder && (
                        <div className="w-[160px] shrink-0 text-sm text-tbl-text-secondary whitespace-nowrap overflow-hidden text-ellipsis px-4" data-node-id="115:16722">
                            {UtilApp.dateStrToDay(item.created, My.user.lang)}
                        </div>
                    )}

                    {!isMessageFolder && (
                        <div className="w-[120px] shrink-0 text-sm text-tbl-text-secondary truncate px-4" data-node-id="115:16724">
                            {item.file ? Util.niceBytes(item.file.sizeByte) : ''}
                        </div>
                    )}

                    <div className="w-8 flex items-center justify-center shrink-0">
                        {!isMessageFolder && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <div
                                        className="p-1.5 rounded-full hover:bg-accent cursor-pointer text-muted-foreground hover:text-[color:rgb(var(--text-body))] transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <MoreVertical className="w-4 h-4" />
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownItems />
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </div>
                {docViewerOpen && (
                    <DocumentViewerModal
                        url={docViewerUrl}
                        title={item.title || item.file?.filename}
                        onClose={() => setDocViewerOpen(false)}
                    />
                )}
            </>
        );
    }

    // Mobile: 카드 형식 (2줄)
    return (
        <>
            <div className="flex items-center gap-3 py-3 border-b border-border bg-background transition-all group">
                <div>
                    <Checkbox
                        checked={checked}
                        onCheckedChange={onCheckedChange}
                        disabled={isMessageFolder}
                        className={cn(isMessageFolder && "opacity-20")}
                    />
                </div>

                <div className="flex-1 min-w-0 flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                        <div className="cursor-pointer shrink-0 relative" onClick={handleOpen}>
                            {isFolder ? (
                                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-muted text-primary">
                                    <Folder className="w-6 h-6 fill-current" />
                                </div>
                            ) : isImage ? (
                                <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted">
                                    <img src={item.url} className="w-full h-full object-contain" alt={item.title} />
                                </div>
                            ) : (
                                <FileIconWithExt filename={item.title || item.file?.filename} size="md" />
                            )}
                            {isOpening && (
                                <div className="absolute inset-0 bg-background/60 flex items-center justify-center rounded-lg">
                                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                                </div>
                            )}
                        </div>

                        <div className="flex-1 font-medium text-[color:rgb(var(--text-body))] truncate cursor-pointer hover:text-primary" onClick={handleOpen}>
                            {item.title || item.url}
                        </div>
                    </div>

                    {!isMessageFolder && (
                        <div className="flex items-center gap-2 text-meta-sm text-muted-foreground">
                            <span className="text-label">{getDisplayName(item.user)}</span>
                            <span className="opacity-50">•</span>
                            <span>{UtilApp.dateStrToDay(item.created, My.user.lang)}</span>
                            {item.file && (
                                <>
                                    <span className="opacity-50">•</span>
                                    <span>{Util.niceBytes(item.file.sizeByte)}</span>
                                </>
                            )}
                            {item.pinned && <Pin className="w-3 h-3 text-primary fill-current" />}
                        </div>
                    )}
                </div>

                {!isMessageFolder && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <div
                                className="p-2 -mr-2 rounded-full active:bg-accent"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <MoreVertical className="w-5 h-5 text-muted-foreground" />
                            </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownItems />
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
            {docViewerOpen && (
                <DocumentViewerModal
                    url={docViewerUrl}
                    title={item.title || item.file?.filename}
                    onClose={() => setDocViewerOpen(false)}
                />
            )}
        </>
    );
};
